<!--
  "איש צוות" on the add / edit user pages: the user's 1:1 link to a crew member (crew_member.userId).
  Crew members are who the events are staffed from, and the events a linked crew member works show
  up for the user (the row scope of a user who doesn't see every event).

  On = the user is linked to a crew member on the crew list: one already there (switching on picks
  the one with the user's email / phone, when there is one) or a new one made from the user's
  details. Off = that crew member leaves the crew list - hidden, like a delete on the crews page:
  the events it already works keep it, and the link stays, so switching back on brings the same
  crew member back (crew-members/for-user restores it), never a second one.

  Nothing is sent while the switch moves: the page calls apply(userId) after its own save.
-->
<template>

    <div v-if="crewsEnabled || linked"
         class="userCrewMemberField">

        <label class="userCrewMemberToggle"
               @click="setOn(!on)">
            <span style="position:relative;top:1px;"
                  class="planup-checkbox planup-checkbox-purple"
                  :class="{ checked: on }">
                <i class="fa fa-check"></i>
            </span>
            איש צוות
        </label>

        <small class="userCrewMemberHint">
            מופיע ברשימת אנשי הצוות וניתן לשבץ אותו לאירועים, ואירועים שהוא משובץ אליהם מופיעים אצלו.
        </small>

        <div v-if="on"
             class="row">
            <div class="col-sm-8">
                <label class="form-label">{{ $t('linkedCrewMember') }}</label>
                <Multiselect v-model="picked"
                             :options="options"
                             label="name"
                             track-by="id"
                             :allow-empty="false"
                             selectedLabel=""
                             deselect-label=""
                             selectLabel=""
                             placeholder="בחרו איש צוות"
                             @select="onPick">
                    <template slot="noResult">לא נמצאו תוצאות</template>
                    <template slot="noOptions">אין אפשרויות</template>
                </Multiselect>
            </div>
        </div>

        <small v-else-if="linked && !linked.hidden"
               class="userCrewMemberOff">
            בשמירה {{ linked.name }} יוסר מרשימת אנשי הצוות. אירועים שכבר שובץ אליהם לא משתנים.
        </small>

    </div>

</template>
<script>

import Multiselect from 'vue-multiselect';
import axiosV2 from '../../core/http/axios_api_v2';
import featureDisabled from '../../core/acl/features';

const NEW_ID = 'new';

function digits(value) {
    return String(value || '').replace(/\D/g, '');
}

export default {

    name: 'UserCrewMemberField',

    components: { Multiselect },

    props: {
        //the user on the page (no id while it is being added): its name / email / phone make a new
        //crew member. Not "user": the global Auth mixin's computed `user` (the logged-in user)
        //overrides a prop of that name.
        formUser: { type: Object, required: true },
    },

    data() {
        return {
            //the crew member linked to the user - also one taken off the crew list (hidden)
            linked: null,
            on: false,
            picked: null,
        }
    },

    computed: {

        //the crews module switched off for the account (root admin) - the switch is shown only
        //to take an existing link off
        crewsEnabled() {
            return !featureDisabled(this.$store, 'crews');
        },

        newOption() {
            return { id: NEW_ID, name: 'איש צוות חדש' + (this.formUser.name ? ' - ' + this.formUser.name : ' לפי פרטי המשתמש') };
        },

        //the linked crew member first (back on the list, when it was taken off), a new one, then
        //every crew member on the list no other user is linked to
        options() {

            let linked = this.linked;
            let options = [];

            if (linked && linked.hidden)
                options.push({ id: linked.id, name: linked.name + ' (יחזור לרשימת אנשי הצוות)' });
            else if (linked)
                options.push(this.listed(linked.id) || linked);

            options.push(this.newOption);

            (this.$store.state.crewMembers || []).forEach(crew => {
                if (linked && crew.id == linked.id)
                    return;
                if (!crew.userId || (this.formUser.id && crew.userId == this.formUser.id))
                    options.push(crew);
            });

            return options;

        },

    },

    watch: {
        'formUser.id': {
            immediate: true,
            handler(id) {
                this.load(id);
            }
        },
        //"a new one" carries the name typed on the page
        newOption(option) {
            if (this.picked && this.picked.id == NEW_ID)
                this.picked = option;
        },
    },

    methods: {

        listed(id) {
            return (this.$store.state.crewMembers || []).find(crew => crew.id == id);
        },

        load(userId) {

            if (!userId) {
                this.linked = null;
                this.on = false;
                this.picked = null;
                return Promise.resolve();
            }

            return axiosV2.get('crew-members/for-user/' + userId).then(res => {

                this.linked = (res.data && res.data.crewMember) || null;

            }).catch(() => {

                //the crew list still knows a link to a crew member that is on it
                this.linked = (this.$store.state.crewMembers || []).find(crew => crew.userId == userId) || null;

            }).then(() => {

                this.on = !!(this.linked && !this.linked.hidden);
                this.picked = this.on ? this.options[0] : null;

            });

        },

        setOn(on) {

            this.on = on;

            if (on && !this.picked)
                this.picked = this.defaultOption();

        },

        //switching on picks the linked crew member, else the one on the list with the user's
        //email / phone, else a new one from the user's details
        defaultOption() {

            if (this.linked)
                return this.options[0];

            let email = String(this.formUser.email || '').trim().toLowerCase();
            let phone = digits(this.formUser.phone);

            let same = this.options.find(option => option.id != NEW_ID && (
                (email && String(option.email || '').trim().toLowerCase() == email) ||
                (phone.length >= 9 && digits(option.phone) == phone)
            ));

            return same || this.newOption;

        },

        //the add-user page fills its empty fields from a crew member picked here
        onPick(option) {
            if (option && option.id != NEW_ID && !(this.linked && option.id == this.linked.id))
                this.$emit('pick', option);
        },

        //Make the server match the switch - called by the page after its own save, with the user's
        //id. Resolves true when something changed; the crew list in the store is reloaded then, so
        //the event pages see it at once.
        apply(userId) {

            let linked = this.linked;
            let picked = this.picked;
            let steps = [];

            if (!this.on) {

                if (linked && !linked.hidden)
                    steps.push(() => axiosV2.delete('crew-members/' + linked.id));

            } else if (picked && linked && picked.id == linked.id) {

                //back on the list
                if (linked.hidden)
                    steps.push(() => axiosV2.post('crew-members/for-user/' + userId, {}));

            } else if (picked) {

                //one crew member per user: the old link goes first
                if (linked)
                    steps.push(() => axiosV2.post('crew-members/' + linked.id + '/unlink', {}));

                if (picked.id == NEW_ID)
                    steps.push(() => axiosV2.post('crew-members/for-user/' + userId, { name: this.formUser.name, email: this.formUser.email, phone: this.formUser.phone }));
                else
                    steps.push(() => axiosV2.post('crew-members/' + picked.id + '/link', { userId: userId }));

            }

            if (!steps.length)
                return Promise.resolve(false);

            return steps.reduce((chain, step) => chain.then(step), Promise.resolve())
                .then(() => this.reloadCrewList())
                .then(() => this.load(userId))
                .then(() => true);

        },

        //take the linked crew member off the crew list (the edit page offers it when the user is
        //disabled)
        hideLinked() {

            if (!this.linked || this.linked.hidden)
                return Promise.resolve(false);

            let userId = this.formUser.id;

            return axiosV2.delete('crew-members/' + this.linked.id)
                .then(() => this.reloadCrewList())
                .then(() => this.load(userId))
                .then(() => true);

        },

        reloadCrewList() {
            return axiosV2.get('crew-members').then(res => {
                this.$set(this.$store.state, 'crewMembers', res.data || []);
            });
        },

    }

}

</script>
<style>
.userCrewMemberField {
    margin-bottom: 12px;
}

.userCrewMemberToggle {
    user-select: none;
    cursor: pointer;
    display: block;
}

.userCrewMemberHint {
    color: #868686;
    display: block;
    margin-bottom: 8px;
}

.userCrewMemberOff {
    color: #b26b00;
    display: block;
}
</style>
